import React, { useEffect, useState } from 'react';
import { Button, Card, Space, Table, Modal, message, Row, Col, Typography, Switch, Input } from 'antd';
import weworkNonChannelWelcomeService from '../services/weworkNonChannelWelcomeService';
import weworkService from '../services/weworkService';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
const { Title, Text } = Typography;

const WeworkNonChannelWelcome = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = { page: Math.max(page - 1, 0), size: pageSize };
      if (keyword && keyword.trim()) params.keyword = keyword.trim();
      const res = await weworkNonChannelWelcomeService.list(params);
      if (res && res.success) {
        setList(res.data || []);
        if (typeof res.total === 'number') setTotal(res.total);
      } else {
        setList([]);
        setTotal(0);
      }
    } catch (e) {
      console.warn('加载成员欢迎语失败:', e);
      // 出错时不抛异常，展示空列表即可
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    // 预取所有员工用于渲染姓名
    (async () => {
      try {
        const emps = await weworkService.getAllEmployees();
        setEmployees(emps || []);
      } catch (e) {
        setEmployees([]);
      }
    })();
  }, []);

  const openCreate = () => {
    navigate('/wework-non-channel-welcome/create');
  };

  const openEdit = (record) => {
    navigate(`/wework-non-channel-welcome/edit/${record.id}`);
  };

  const onRemove = async (record) => {
    Modal.confirm({
      title: '确认删除？',
      onOk: async () => {
        const res = await weworkNonChannelWelcomeService.remove(record.id);
        if (res.success) { message.success('已删除'); load(); }
        else message.error(res.message || '删除失败');
      }
    });
  };

  const onToggleStatus = async (record, checked) => {
    const nextStatus = checked ? 1 : 0;
    const hide = message.loading('正在更新状态...', 0);
    try {
      const payload = {
        id: record.id,
        title: record.title,
        userIds: record.userIds,
        textContent: record.textContent,
        attachments: Array.isArray(record.attachments) ? record.attachments : [],
        status: nextStatus
      };
      const res = await weworkNonChannelWelcomeService.save(payload);
      if (res.success) {
        message.success('状态已更新');
        // 本地更新，避免全量刷新
        setList(prev => prev.map(item => item.id === record.id ? { ...item, status: nextStatus } : item));
      } else {
        message.error(res.message || '更新失败');
      }
    } catch (e) {
      message.error('更新失败');
    } finally {
      hide();
    }
  };

  // 无弹窗表单，这里不需要保存处理函数

  const columns = [
    { title: '名称', dataIndex: 'title' },
    {
      title: '生效成员',
      render: (_, record) => {
        const rawUserIds = record.userIds;

        // 解析 userIds，兼容数组或字符串(JSON)
        let parsedUserIds = [];
        if (Array.isArray(rawUserIds)) {
          parsedUserIds = rawUserIds;
        } else if (typeof rawUserIds === 'string') {
          try {
            parsedUserIds = JSON.parse(rawUserIds) || [];
          } catch (e) {
            parsedUserIds = [];
          }
        }

        if (!Array.isArray(parsedUserIds) || parsedUserIds.length === 0) {
          return <span style={{ color: '#999' }}>未选择</span>;
        }

        // 将ID映射为姓名，优先按企微 userid 匹配
        const displayUsers = parsedUserIds.map((userId) => {
          const foundByUserid = (employees || []).find(emp => emp.userid === userId || emp.userid === String(userId));
          if (foundByUserid) {
            return foundByUserid.name || foundByUserid.displayName || foundByUserid.nickname || userId;
          }
          const foundBySystemId = (employees || []).find(emp => emp.id === userId || String(emp.id) === String(userId));
          if (foundBySystemId) {
            return foundBySystemId.name || foundBySystemId.displayName || foundBySystemId.nickname || userId;
          }
          return `${userId}(未匹配)`;
        });

        return (
          <div>
            {displayUsers.length <= 3 ? (
              displayUsers.map((user, index) => (
                <div key={index} style={{ fontSize: '12px', color: '#1890ff' }}>{user}</div>
              ))
            ) : (
              <>
                {displayUsers.slice(0, 2).map((user, index) => (
                  <div key={index} style={{ fontSize: '12px', color: '#1890ff' }}>{user}</div>
                ))}
                <div style={{ fontSize: '12px', color: '#999' }}>+{displayUsers.length - 2}位成员</div>
              </>
            )}
          </div>
        );
      }
    },
    {
      title: '状态', dataIndex: 'status', render: (v, r) => (
        <Switch checked={v === 1} onChange={(checked) => onToggleStatus(r, checked)} />
      )
    },
    {
      title: '操作',
      render: (_, r) => (
        <Space>
          <Button type="link" onClick={() => openEdit(r)}>编辑</Button>
          <Button type="link" danger onClick={() => onRemove(r)}>删除</Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>成员欢迎语</Title>
          <Text type="secondary">
            微信顾客不是通过“成员活码”而是“群聊、名片分享等”添加员工企微，将在添加员工企微后收到该欢迎语。
          </Text>
        </Col>
      </Row>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            enterButton
            placeholder="搜索名称"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSearch={() => load()}
            style={{ width: 260 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增</Button>
        </div>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={list}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            }
          }}
        />
      </Card>
    </div>
  );
};

export default WeworkNonChannelWelcome;


